import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Button, Empty, Modal, Spin, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import {
  ProgressLogEntry,
  QuestProgressItem,
  QuestProgressStatus,
  useGetProgressLog,
} from "src/hooks/affiliate";

const { Text } = Typography;

const statusColor: Record<string, string> = {
  [QuestProgressStatus.IN_PROGRESS]: "blue",
  [QuestProgressStatus.COMPLETED]: "green",
  [QuestProgressStatus.REWARDED]: "gold",
};

const actionColor: Record<string, string> = {
  INCREMENT: "blue",
  DECREMENT: "orange",
  COMPLETE: "green",
  REWARD: "gold",
  RESET: "red",
};

export type ProgressLogModalProps = {
  progress: QuestProgressItem;
};

export const ProgressLogModal = NiceModal.create(({ progress }: ProgressLogModalProps) => {
  const { visible, hide, remove } = useModal();
  const { data, isFetching } = useGetProgressLog(progress.uid);

  return (
    <Modal
      centered
      width={800}
      visible={visible}
      afterClose={remove}
      onCancel={hide}
      title={`ประวัติ Progress: ${progress.questName}`}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px" }}>
          <Button size="large" onClick={hide} style={{ width: 100 }}>
            ปิด
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Campaign:</Text> <Tag>{progress.campaignCode}</Tag>{" "}
        <Text type="secondary">Quest:</Text> {progress.questName}{" "}
        <Text type="secondary">สถานะปัจจุบัน:</Text>{" "}
        <Tag color={statusColor[progress.status]}>{progress.status}</Tag>{" "}
        <Text type="secondary">Current/Target:</Text> {progress.currentValue} /{" "}
        {progress.targetValue}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
        {isFetching && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        )}
        {!isFetching && (!data || !data.items.length) && (
          <Empty description="ยังไม่มีประวัติการเปลี่ยนแปลง" />
        )}
        {!isFetching && data && data.items.length > 0 && (
          <Timeline>
            {data.items.map((entry: ProgressLogEntry) => (
              <Timeline.Item
                key={entry.uid}
                color={actionColor[entry.actionType] || "blue"}
              >
                <div>
                  <Tag color={actionColor[entry.actionType] || "blue"}>
                    {entry.actionType}
                  </Tag>
                  <Text strong>
                    {entry.oldValue} → {entry.newValue}
                  </Text>{" "}
                  <Text type="secondary">
                    (delta {entry.delta >= 0 ? `+${entry.delta}` : entry.delta})
                  </Text>
                </div>
                {(entry.oldStatus || entry.newStatus) &&
                  entry.oldStatus !== entry.newStatus && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">สถานะ:</Text>{" "}
                      {entry.oldStatus ? (
                        <Tag color={statusColor[entry.oldStatus]}>{entry.oldStatus}</Tag>
                      ) : (
                        <Tag>-</Tag>
                      )}
                      <Text type="secondary"> → </Text>
                      {entry.newStatus ? (
                        <Tag color={statusColor[entry.newStatus]}>{entry.newStatus}</Tag>
                      ) : (
                        <Tag>-</Tag>
                      )}
                    </div>
                  )}
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    {dayjs(entry.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                  </Text>
                  {entry.updatedBy && (
                    <>
                      {" • "}
                      <Text type="secondary">โดย</Text> {entry.updatedBy}
                    </>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    downline: {entry.psCode || "-"}
                  </Text>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </div>
    </Modal>
  );
});

export default ProgressLogModal;
